import styled, { css } from 'styled-components';

const FormFieldError = styled.p`
  position: absolute;
  top: calc(100% + 4px);
  left: 20px;
  font-size: ${(props) => props.theme.fonts.body3.fontSize};
  line-height: ${(props) => props.theme.fonts.body3.lineHeight};
  color: ${(props) => props.theme.colors.danger};
`;

interface FormFieldWrapProps {
  isError?: boolean;
  hasLabel?: boolean;
  disabled?: boolean;
}

const FormFieldWrap = styled.label<FormFieldWrapProps>`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;

  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.6;
      pointer-events: none;
    `};
`;

const RadioWrap = styled(FormFieldWrap)<{ isDisabled?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: start;

  ${(props) => props.isDisabled && 'opacity: 0.5'};
`;

const RadioInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  padding: 0;
  border: none;
  position: absolute;
  z-index: -1;
`;

const Radio = styled.div<{
  isChecked?: boolean;
  isDisabled?: boolean;
  isError?: boolean;
  isSurvey?: boolean;
}>`
  width: 18px;
  min-width: 18px;
  height: 18px;
  min-height: 18px;
  display: flex;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${(props) => props.theme.colors.primary_lightest};
  box-sizing: border-box;
  opacity: 0.5;
  transition: box-shadow 0.2s linear;

  ${(props) =>
    props.isSurvey &&
    css`
      border-color: ${props.theme.colors.black};
    `}

  ${(props) =>
    props.isError &&
    css`
      border-color: ${props.theme.colors.danger};
    `}

  ${(props) =>
    props.isDisabled &&
    css`
      border-color: ${props.theme.colors.gray4};
    `}

  ${(props) =>
    props.isChecked &&
    props.isSurvey &&
    !props.isDisabled &&
    css`
      background: radial-gradient(
        ${props.theme.colors.gray3} 0%,
        ${props.theme.colors.gray2} 50%,
        transparent 60%,
        transparent
      );
      border-color: ${props.theme.colors.black};
      opacity: 1;
      display: inline-block;
      cursor: pointer;
    `}

  ${(props) =>
    props.isChecked &&
    !props.isSurvey &&
    !props.isDisabled &&
    css`
      background: radial-gradient(
        ${props.theme.colors.primary_lighter} 0%,
        ${props.theme.colors.primary_lighter} 50%,
        transparent 60%,
        transparent
      );
      border-color: ${props.theme.colors.primary_lighter};
      opacity: 1;
      display: inline-block;
      cursor: pointer;
    `}

  ${(props) =>
    props.isChecked &&
    props.isDisabled &&
    css`
      background: radial-gradient(
        ${props.theme.colors.gray4} 0%,
        ${props.theme.colors.gray4} 50%,
        transparent 60%,
        transparent
      );
      border-color: ${props.theme.colors.gray4};
      opacity: 1;
      display: inline-block;
      cursor: pointer;
    `}

  ${(props) =>
    !props.isChecked &&
    !props.isDisabled &&
    css`
      &:hover {
        box-shadow: inset 0 0 0 1px ${props.theme.colors.primary_lightest};
      }
    `}
`;

const RadioLabel = styled.label<{ labelSide?: 'left' | 'right'; isError?: boolean }>`
  cursor: pointer;
  ${({ labelSide = 'right', theme }) => `margin-${labelSide === 'left' ? 'right' : 'left'}: ${theme.spaces.s12}`};
  font-size: ${(props) => props.theme.fonts.body3.fontSize_md};
  line-height: ${(props) => props.theme.fonts.body3.lineHeight_md};

  ${(props) =>
    props.labelSide === 'left' &&
    css`
      order: -1;
    `}

  ${(props) =>
    props.isError &&
    css`
      color: ${props.theme.colors.danger};
    `}
`;
const RadioSurveyWrap = styled(FormFieldWrap)<{ isDisabled?: boolean }>`
  display: flex;
  align-items: start;
  cursor: pointer;

  ${(props) => props.isDisabled && 'opacity: 0.5'};
`;
const RadioSurvey = styled.div<{
  isChecked?: boolean;
  isDisabled?: boolean;
  isError?: boolean;
  isSurvey?: boolean;
}>`
  border: 2px solid black;
  padding: 10px 15px;
  width: 100%;
  border-radius: 8px;
  text-align: center;
  background-image: linear-gradient(to top, rgba(144, 202, 229, 0.5) 50%, transparent 50%);
  background-size: 100% 200%;
  background-position: top;
  transition: all 0.2s linear;

  ${(props) =>
    props.isError &&
    css`
      border-color: ${props.theme.colors.danger};
    `}

  ${(props) =>
    props.isDisabled &&
    css`
      border-color: ${props.theme.colors.gray4};
    `}

  ${(props) =>
    props.isChecked &&
    css`
      border-color: ${props.theme.colors.primary_lighter};
      background-position: bottom;
    `}
`;
const RadioSurveyLabel = styled.label<{ isError?: boolean }>`
  font-size: ${(props) => props.theme.fonts.body2.fontSize_md};
  line-height: ${(props) => props.theme.fonts.body2.lineHeight_md};
  cursor: pointer;

  ${(props) =>
    props.isError &&
    css`
      color: ${props.theme.colors.danger};
    `}
`;

const RadioSurveySelected = styled.div`
  position: absolute;
  top: -7px;
  right: -7px;
  width: 20px;
  height: 20px;
  background-color: ${(props) => props.theme.colors.primary_lighter};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 12px;
    height: 12px;
  }
`;

export {
  FormFieldError,
  RadioInput,
  RadioWrap,
  RadioSurveyWrap,
  Radio,
  RadioSurvey,
  RadioLabel,
  RadioSurveyLabel,
  RadioSurveySelected,
};
